import React, { useState } from "react";
import Form from "../../../HelperComponents/Form/Form";
import { Icon } from "@iconify-icon/react";
import LoadingSpinner from "../../../HelperComponents/LoadingSpinner";

interface UpdateBankingInfoProps {
  userProfile?: any;
  user: any;
}

const styles = {
  container: {
    fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
    padding: "12px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(1fr, 1fr))",
    gridGap: "15px",
    alignItems: "start",
  },
  card: {
    backgroundColor: "hsl(var(--background))",
    borderRadius: "8px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    padding: "20px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "hsl(var(--primary))",
    paddingBottom: "8px",
    borderBottom: "1px solid hsl(var(--border))",
    marginBottom: "12px",
  },
};

const UpdateBankingInfo = (props: UpdateBankingInfoProps) => {
  const [loading] = useState(false);

  const fields = [
    {
      id: "socialsecuritynumber",
      title: "Social Security Number",
      type: "Text",
      value: props.userProfile?.Socialsecuritynumber,
      visible: true,
      required: true,
      placeholder: "XXX-XX-XXXX",
      errorMessage: "Valid format: XXX-XX-XXXX",
      destinations: [
        { destination: "userProfile", fieldName: "Socialsecuritynumber" },
        {
          destination: "applicants",
          fieldName: "Social_x0020_Security_x0020_Numb",
        },
      ],
    },
    {
      id: "name",
      title: "Name (As shown on tax return):",
      type: "Text",
      visible: true,
      value: props.userProfile?.CurrentTaxReportingName,
      required: true,
      destinations: [
        { destination: "userProfile", fieldName: "CurrentTaxReportingName" },
      ],
    },
    {
      id: "routingNumber",
      title: "Routing Number",
      type: "Text",
      visible: true,
      required: true,
      value: props.userProfile?.Routingnumber || "",
      destinations: [
        { destination: "userProfile", fieldName: "RoutingNumber" },
      ],
    },
    {
      id: "bankName",
      title: "Bank Name",
      value: props.userProfile?.Bankname,
      type: "Text",
      readOnly: true,
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "Bankname" }],
    },
    {
      id: "bankAccountNumber",
      title: "Bank Account Number",
      value: props.userProfile?.Bankaccountnumber,
      type: "Text",
      visible: true,
      required: true,
      destinations: [
        { destination: "userProfile", fieldName: "Bankaccountnumber" },
      ],
    },
    {
      id: "socialNumberForReportingCheckbox",
      title: `Use ${props.user?.Country === "Canada" ? "Social Insurance Number" : "Social Security Number"} for Reporting (If filing as an individual)`,
      type: "Checkbox",
      value: props.userProfile?.UseSSNForReporting,
      description: "Check appropriate box for federal tax classification.",
      visible: true,
      destinations: [
        {
          destination: "userProfile",
          fieldName: "UseSSNForReporting",
        },
      ],
    },
    {
      id: "taxIdNumber",
      title: "Employer Id. No. (If filing as a business)",
      type: "Text",
      value: props.userProfile?.TaxIDnumber,
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "TaxIDnumber" }],
    },
    {
      id: "businessName",
      title: "Business Name (If filing as a business)",
      type: "Text",
      value: props.userProfile?.Businessname,
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "Businessname" }],
    },
    {
      id: "businessClassification",
      title: "Business Classification",
      type: "List",
      value: props.userProfile?.BusinessClassification,
      listName: "BusinessClassifications",
      dropdownText: ["name"],
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "BusinessClassification" },
      ],
    },
    {
      id: "businessAddressCheckbox",
      title: "Use Current Address for Reporting",
      type: "Checkbox",
      description: "If No, please fill out the Alternate Address Fields below.",
      value: props.userProfile?.UseCurrentAddressForReporting,
      visible: true,
      destinations: [
        {
          destination: "userProfile",
          fieldName: "UseCurrentAddressForReporting",
        },
      ],
    },
    {
      id: "businessAddress",
      title: "Business Address",
      type: "Text",
      description: `This Field should be filled out if you have not checked for "Use Current address for Reporting"`,
      value: props.userProfile?.BusinessAddress,
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "BusinessAddress" },
      ],
      visibilityConditions: [
        {
          fieldId: "businessAddressCheckbox",
          operator: "notChecked",
        },
      ],
    },
    {
      id: "businessCity",
      title: "Business City",
      type: "Text",
      description: `This Field should be filled out if you have not checked for "Use Current address for Reporting"`,
      value: props.userProfile?.BusinessCity,
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "BusinessCity" }],
      visibilityConditions: [
        {
          fieldId: "businessAddressCheckbox",
          operator: "notChecked",
        },
      ],
    },
    {
      id: "businessState",
      title: "Business State",
      type: "List",
      listName: "State",
      dropdownText: ["Code"],
      description: `This Field should be filled out if you have not checked for "Use Current address for Reporting"`,
      value: props.userProfile?.BusinessState,
      visible: true,
      destinations: [
        { destination: "userProfile", fieldName: "BusinessState" },
      ],
      visibilityConditions: [
        {
          fieldId: "businessAddressCheckbox",
          operator: "notChecked",
        },
      ],
    },
    {
      id: "businessZip",
      title: "Business Zip",
      type: "Text",
      description: `This Field should be filled out if you have not checked for "Use Current address for Reporting"`,
      value: props.userProfile?.BusinessZip,
      visible: true,
      destinations: [{ destination: "userProfile", fieldName: "BusinessZip" }],
      visibilityConditions: [
        {
          fieldId: "businessAddressCheckbox",
          operator: "notChecked",
        },
      ],
    },
  ];

  return (
    <div className="contentContainer">
      <div key={1} className="content">
        <h2 className="title">
          <Icon className="cardIcon" icon="lucide:edit" />
          Update Banking Info
        </h2>
        <div className="contentCard">
          <div style={styles.container}>
            <div style={styles.grid}>
              <div style={styles.card}>
                <h3 style={styles.sectionTitle}>Banking and Secure Info</h3>
                {loading && <LoadingSpinner />}
                {!loading && <Form fields={fields} columns={3} mode="update" />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateBankingInfo;
